import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Truck,
  CreditCard,
  Phone,
  Mail,
  ShieldCheck,
  FileText,
  Building2,
  PhoneCall,
  LogOut,
  ExternalLink,
  CheckCircle2,
  Bell,
  MapPin,
  Smartphone,
  Check,
  AlertCircle
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { FirebaseAuthService } from '../../services/firebaseAuthService';
import { pushNotificationService } from '../../services/pushNotificationService';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { empresaService } from '../../services/empresaService';

export const AreaMotoristaPerfil: React.FC = () => {
  const { currentMotorista, isAgregado, enviarLocalizacaoGps, transmitindoGps } = useAreaMotorista();
  const { isInstalled, isInstallable, install } = usePWAInstall();
  const navigate = useNavigate();

  const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [gpsPermission, setGpsPermission] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const [gpsFeedback, setGpsFeedback] = useState<string | null>(null);
  // Telefone e WhatsApp de suporte vêm de Configurações do Sistema > Dados da
  // Empresa; só caem nos números de exemplo se nada foi configurado lá.
  const [telefoneSuporte, setTelefoneSuporte] = useState<string>('0800 000 0000');
  const [whatsappSuporte, setWhatsappSuporte] = useState<string>('5511999999999');

  useEffect(() => {
    empresaService.getEmpresaAtiva()
      .then((empresa) => {
        if (empresa?.telefone) setTelefoneSuporte(empresa.telefone);
        if (empresa?.whatsapp) {
          const digitos = empresa.whatsapp.replace(/[^\d]/g, '');
          // wa.me exige código do país (55) na frente do DDD; o campo é salvo
          // sem ele (ex.: "(11) 94711-5843"), então adiciona se faltar.
          setWhatsappSuporte(digitos.startsWith('55') ? digitos : `55${digitos}`);
        }
      })
      .catch((err) => console.warn('Erro ao carregar contatos de suporte:', err));
  }, []);

  useEffect(() => {
    setNotifPermission(pushNotificationService.getPermissionStatus());

    if (typeof navigator !== 'undefined' && 'permissions' in navigator && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
        setGpsPermission(result.state as 'prompt' | 'granted' | 'denied');
        result.onchange = () => {
          setGpsPermission(result.state as 'prompt' | 'granted' | 'denied');
        };
      }).catch(() => {
        setGpsPermission('unknown');
      });
    }
  }, []);

  const handleRequestNotif = async () => {
    const granted = await pushNotificationService.requestPermission();
    setNotifPermission(pushNotificationService.getPermissionStatus());
    if (granted) {
      pushNotificationService.sendNotification('🔔 Notificações Ativadas!', {
        body: 'Alertas operacionais ativos para este dispositivo.',
      });
    }
  };

  const handleRequestGPS = async () => {
    setGpsFeedback('Testando sinal...');
    const res = await enviarLocalizacaoGps();
    if (res.success) {
      setGpsPermission('granted');
      setGpsFeedback('Localização transmitida!');
    } else {
      setGpsFeedback(res.message || 'Erro ao obter sinal');
    }
    setTimeout(() => setGpsFeedback(null), 3500);
  };

  const handleLogout = async () => {
    await FirebaseAuthService.logout();
    navigate('/login');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 text-left">
      
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
          <User className="w-6 h-6 text-red-500" />
          <span>Meu Perfil & Veículo</span>
        </h1>
        <p className="text-xs text-slate-400">
          Dados cadastrais, identificação veicular e canais de atendimento.
        </p>
      </div>

      {/* Driver Identity Card */}
      <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 shadow-2xl space-y-5">
        
        <div className="flex items-center gap-4 border-b border-slate-800 pb-4">
          <div className="w-14 h-14 rounded-2xl bg-red-600/20 text-red-400 border border-red-500/30 flex items-center justify-center font-black text-xl">
            {currentMotorista?.nome?.charAt(0) || 'M'}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-black text-white">{currentMotorista?.nome}</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-950 text-emerald-400 border border-emerald-800">
                Ativo
              </span>
            </div>
            <p className="text-xs text-slate-400">{currentMotorista?.tipo_vinculo || 'Motorista Parceiro'}</p>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          
          <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
              Documento (CPF / CNPJ)
            </span>
            <p className="font-semibold text-slate-200 font-mono">{currentMotorista?.documento || 'Não informado'}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Phone className="w-3.5 h-3.5 text-slate-400" />
              Telefone / WhatsApp
            </span>
            <p className="font-semibold text-slate-200">{currentMotorista?.fone || 'Não informado'}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Mail className="w-3.5 h-3.5 text-slate-400" />
              E-mail Cadastrado
            </span>
            <p className="font-semibold text-slate-200 truncate">{currentMotorista?.email || 'Não informado'}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <CreditCard className="w-3.5 h-3.5 text-slate-400" />
              {isAgregado ? 'Chave PIX para Repasses' : 'Remuneração Operacional'}
            </span>
            {isAgregado ? (
              <p className="font-semibold text-purple-300 font-mono">
                {currentMotorista?.chave_pix || currentMotorista?.documento || 'Chave PIX (CPF)'}
              </p>
            ) : (
              <p className="font-semibold text-blue-300">
                Folha de Pagamento Interna (CLT)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Linked Vehicle Specs */}
      <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 sm:p-6 shadow-xl space-y-4">
        <h2 className="text-sm font-bold text-white flex items-center gap-2">
          <Truck className="w-4 h-4 text-red-500" />
          <span>Veículo Vinculado</span>
        </h2>

        {currentMotorista?.placa ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Placa</span>
              <p className="text-base font-black text-white font-mono">{currentMotorista.placa}</p>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Tipo / Frota</span>
              <p className="font-semibold text-slate-200">{currentMotorista.tipo_vinculo || 'Agregado'}</p>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status Operacional</span>
              <p className="font-semibold text-emerald-400">Liberado para Viagens</p>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-xs text-slate-400 space-y-1">
            <p className="font-semibold text-slate-300">Veículo alocado dinamicamente por Ordem de Serviço</p>
            <p className="text-[11px]">A identificação da placa e modelo é carregada em cada frete individual.</p>
          </div>
        )}
      </div>

      {/* PWA & Device Permissions Card */}
      <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 sm:p-6 shadow-xl space-y-4">
        <h2 className="text-sm font-bold text-white flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-red-500" />
          <span>Configuração PWA & Permissões do Aparelho</span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          {/* Permissão de Notificações */}
          <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-200 flex items-center gap-1.5">
                  <Bell className="w-4 h-4 text-amber-400" />
                  Notificações Push
                </span>
                {notifPermission === 'granted' ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-950 text-emerald-400 border border-emerald-800 flex items-center gap-1">
                    <Check className="w-2.5 h-2.5" /> Ativo
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-950 text-amber-400 border border-amber-800">
                    Pendente
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400">
                Receba alertas sonoros e notificações de novas ordens de serviço diretamente na tela bloqueada.
              </p>
            </div>

            <button
              onClick={handleRequestNotif}
              className={`w-full py-2 px-3 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition cursor-pointer ${
                notifPermission === 'granted'
                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                  : 'bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-sm'
              }`}
            >
              <Bell className="w-3.5 h-3.5" />
              <span>{notifPermission === 'granted' ? 'Enviar Teste de Notificação' : 'Solicitar Permissão de Notificação'}</span>
            </button>
          </div>

          {/* Permissão de Localização GPS */}
          <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-200 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  Localização em Tempo Real (GPS)
                </span>
                {gpsPermission === 'granted' ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-950 text-emerald-400 border border-emerald-800 flex items-center gap-1">
                    <Check className="w-2.5 h-2.5" /> Ativo
                  </span>
                ) : gpsPermission === 'denied' ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-950 text-red-400 border border-red-800">
                    Bloqueado
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 text-slate-400">
                    Disponível
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400">
                Transmite a telemetria da rota e comprova paradas de coleta/entrega para a central de tráfego.
              </p>
            </div>

            <button
              onClick={handleRequestGPS}
              disabled={transmitindoGps}
              className={`w-full py-2 px-3 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition cursor-pointer ${
                gpsPermission === 'granted'
                  ? 'bg-blue-950/80 hover:bg-blue-900 border border-blue-700/60 text-blue-300'
                  : 'bg-blue-600 hover:bg-blue-500 text-white shadow-sm'
              }`}
            >
              <MapPin className={`w-3.5 h-3.5 ${transmitindoGps ? 'animate-spin' : ''}`} />
              <span>{transmitindoGps ? 'Transmitindo...' : gpsFeedback || 'Solicitar / Testar Sinal GPS'}</span>
            </button>
          </div>
        </div>

        {/* Status de Instalação PWA */}
        <div className="p-3.5 rounded-2xl bg-slate-900/60 border border-slate-800 flex items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2.5">
            <Smartphone className="w-4 h-4 text-red-400 shrink-0" />
            <div>
              <p className="font-bold text-slate-200">Aplicativo Progressivo (PWA)</p>
              <p className="text-[11px] text-slate-400">
                {isInstalled ? 'App instalado como aplicativo nativo.' : 'Pode ser instalado na tela de início do celular.'}
              </p>
            </div>
          </div>
          {!isInstalled && isInstallable && (
            <button
              onClick={install}
              className="px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-black text-xs shrink-0 transition cursor-pointer"
            >
              Instalar Agora
            </button>
          )}
        </div>
      </div>

      {/* Support & Operational Hotline */}
      <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 sm:p-6 shadow-xl space-y-4">
        <h2 className="text-sm font-bold text-white flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-emerald-400" />
          <span>Central de Apoio & Tráfego Voope</span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <a
            href={`https://wa.me/${whatsappSuporte}?text=Ol%C3%A1%20Central%20Voope%2C%20sou%20motorista%20e%20preciso%20de%20apoio`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-3.5 rounded-2xl bg-emerald-950/40 hover:bg-emerald-950/70 border border-emerald-800/60 text-emerald-300 font-bold flex items-center justify-between transition"
          >
            <div className="space-y-0.5">
              <span>WhatsApp de Operações</span>
              <p className="text-[11px] text-emerald-400/80 font-normal">Tráfego & Logística em Tempo Real</p>
            </div>
            <ExternalLink className="w-4 h-4" />
          </a>

          <a
            href={`tel:${telefoneSuporte.replace(/[^\d+]/g, '')}`}
            title={`Ligar para ${telefoneSuporte}`}
            className="p-3.5 rounded-2xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-200 font-bold flex items-center justify-between transition"
          >
            <div className="space-y-0.5">
              <span>Telefone de Emergência 24h</span>
              <p className="text-[11px] text-slate-400 font-normal">Sinistros, socorro mecânico e apoio</p>
            </div>
            <Phone className="w-4 h-4 text-slate-400" />
          </a>
        </div>
      </div>

      {/* System Navigation & Logout */}
      <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="w-full sm:flex-1 py-3 px-4 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs flex items-center justify-center gap-2 border border-slate-700 transition"
        >
          <Building2 className="w-4 h-4" />
          <span>Voltar ao Sistema Voope Two</span>
        </button>

        <button
          onClick={handleLogout}
          className="w-full sm:w-auto py-3 px-5 rounded-2xl bg-red-950/40 hover:bg-red-950/70 text-red-400 border border-red-800/60 font-bold text-xs flex items-center justify-center gap-2 transition"
        >
          <LogOut className="w-4 h-4" />
          <span>Sair da Conta</span>
        </button>
      </div>

    </div>
  );
};
